import type { IDropdownConfig, TypeBuildReturn } from './types';

import { merge } from '../../helper';

import addClass from '../../general/class/addClass';
import appendChildAtIndex from '../../general/append/appendChildAtIndex';
import isEmptyObject from '../../general/isEmpty/isEmptyObject';
import removeClass from '../../general/class/removeClass';

import exExternal from '../../external';
import * as exButton from '../button';
import * as exElement from '../../utils/element';
import * as exEvent from '../../utils/event';
import * as exIcon from '../../utils/icon';
import * as exLink from '../../utils/link';

import * as exDropdownMenu from './menu';

// ----------------------------------------------------------------------------

function create(config: IDropdownConfig = {}): TypeBuildReturn {
    // Default Config
    const defConfig: IDropdownConfig = {
        bootstrap: {
            enabled: true,
        },
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    //
    const build = {
        elements: {},
    } as TypeBuildReturn;

    // ------------------------------------------------------------------------

    // Create element (dropdown)
    const elDropdown = exElement.create({
        merge: {
            ...defConfig,
            event: null,
        },
        class: {
            new: 'dropdown',
        },
    });
    build.elements.element = elDropdown;
    build.element = elDropdown;

    // ------------------------------------------------------------------------

    // If using dropdown link
    if (!isEmptyObject(defConfig.link)) {
        // Create element (link)
        const link = exLink.create({
            merge: defConfig.link,
            attr: {
                role: 'button',
            },
            class: {
                new: 'dropdown-link',
            },
        });
        elDropdown.appendChild(link.element);
        build.elements.link = link;
    }
    // If using dropdown button
    else if (!isEmptyObject(defConfig.button)) {
        // Create element (button)
        const button = exButton.create({
            merge: defConfig.button,
            class: {
                new: 'dropdown-button',
            },
        });
        elDropdown.appendChild(button.element);
        build.elements.button = button;
    }

    // If using dropdown link or dropdown button
    if (build.elements.link || build.elements.button) {
        const item = build.elements.link || build.elements.button;

        if (!item) {
            return build;
        }

        // If using dropdown toggle
        if (defConfig.link?.toggle === true || defConfig.button?.toggle === true) {
            addClass(item.element, 'dropdown-toggle');
            item.element.dataset.bsToggle = 'dropdown';
        }

        // If using dropdown submenu
        if (defConfig.submenu) {
            addClass(item.element, 'dropdown-item');
        }

        // If not using dropdown submenu or dropdown split
        if (!defConfig.submenu && !defConfig.split) {
            item.element.dataset.bsToggle = 'dropdown';
        }

        //
        if (item.element.dataset.bsToggle === 'dropdown') {
            build.elements.toggle = item.element;
        }
    }

    // ------------------------------------------------------------------------

    // If using dropdown menu
    if (!isEmptyObject(defConfig.menu) || defConfig.menu === true) {
        // Create element (dropdown menu)
        const dropdownMenu = exDropdownMenu.create({
            merge: defConfig.menu,
        });
        elDropdown.appendChild(dropdownMenu.element);
        build.elements.menu = dropdownMenu;
    }

    // ------------------------------------------------------------------------

    // Settings
    settings(build, defConfig);

    // Events
    events(build, defConfig);

    // ------------------------------------------------------------------------

    // If using bootstrap
    if (defConfig.bootstrap?.enabled === true) {
        // Bootstrap (dropdown)
        const bsDropdown = new exExternal.bootstrap.Dropdown(build.elements.toggle, defConfig.bootstrap);
        build.bs = bsDropdown;
    }

    // ------------------------------------------------------------------------

    // Return
    return build;
}

function settings(build: TypeBuildReturn, config: Record<string, any> = {}): void {
    const elements = build.elements;

    // If using dropdown split
    if (!isEmptyObject(config.split) || config.split === true) {
        // Create element (dropdown split)
        const split = exLink.create({
            merge: config.split,
            class: {
                new: 'dropdown-toggle dropdown-toggle-split',
            },
            dataset: {
                bsToggle: 'dropdown',
            },
        });
        appendChildAtIndex(elements.element, split.element, 1);
        elements.split = split;

        // If using split icon
        if (!isEmptyObject(config.split.icon) || config.split.icon === true) {
            // Remove class (dropdown toggle)
            removeClass(split.element, 'dropdown-toggle');

            // Create element (icon)
            const elIcon = exIcon.create({
                merge: config.split.icon,
                class: {
                    new: 'fa-solid fa-angle-down',
                },
            });
            split.element.appendChild(elIcon);
        }
        // Is default
        else {
            // Create element (span)
            const elSpan = exElement.create({
                tag: 'span',
                class: {
                    new: 'visually-hidden',
                },
                html: 'Toggle Dropdown',
            });
            split.element.appendChild(elSpan);
        }

        //
        if (split.element.dataset.bsToggle === 'dropdown') {
            elements.toggle = split.element;
        }

        //
        addClass(elements.element, 'btn-group');
    }

    // ------------------------------------------------------------------------

    // If using dropdown submenu
    if (!isEmptyObject(config.submenu) || config.submenu === true) {
        removeClass(elements.element, 'dropdown');
        addClass(elements.element, 'dropdown-submenu');

        // If using direction
        if (config.submenu?.direction) {
            // Add class direction
            addClass(elements.element, config.submenu.direction);
        }
        // Is default
        else {
            addClass(elements.element, 'dropend');
        }

        // Function Action
        const fnAction = function (evt: Event): any {
            evt.preventDefault();
            evt.stopPropagation();
        };

        // If using split
        if (elements.split?.element) {
            // Create event (click)
            elements.split.element.addEventListener('click', fnAction);
        }
        // If using link
        else if (elements.link?.element) {
            addClass(elements.link.element, 'dropdown-toggle');
            elements.link.element.dataset.bsToggle = 'dropdown';

            //
            if (elements.link.element.dataset.bsToggle === 'dropdown') {
                elements.toggle = elements.link.element;
            }

            // Create event (click)
            elements.link.element.addEventListener('click', fnAction);
        }
    }
}

function events(build: TypeBuildReturn, config: Record<string, any> = {}): void {
    const element = build.element;

    // Create event (show dropdown)
    element.addEventListener('show.bs.dropdown', function (this: HTMLElement): any {
        // If using dataset boundary
        if (this.dataset.bsBoundary === 'fixed') {
            const elDropdownMenu = this.querySelector<HTMLElement>('.dropdown-menu');
            if (elDropdownMenu) {
                addClass(elDropdownMenu, 'position-fixed');
            }
        }
    });

    // ------------------------------------------------------------------------

    // If using event
    if (config.event != null) {
        exEvent.create(element, config.event);
    }
}

// ----------------------------------------------------------------------------

export { create };
